import { Component } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppLoginPrincipalService, AppLoginSubjectService, LinkSubjectToTeamService, Pagination } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../../modules/permission/facades/permission.facade';
import { TeamMemberSearchModel } from '../../../../../../models/advanced-search-models/team-member-search.model';
import { SearchQueryModel } from '../../../../../../models/query.model';
import { MemberModel } from '../../../../../../models/team-permissions.model';
import { ViewColumnComponent } from '../../../../../../view-column.component';
import { AppConfig } from '../../../../../../visa';
import { combineLatest, forkJoin, iif, Observable, of } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../../permission-controller/root';
import { TeamPermActions } from '../../../../team-permissions-controller/actions';
import { TeamPermSelectors } from '../../../../team-permissions-controller/selectors';
import { TeamPermStates } from '../../../../team-permissions-controller/states';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-team-permission-members',
  templateUrl: './team-permission-members.component.html',
  styleUrls: ['./team-permission-members.component.scss']
})

export class TeamPermissionMembersComponent extends ViewColumnComponent {

  protected readonly index = 1;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  deactivateAdd$;
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  // api visa
  apiVisa = AppConfig.apiVisa;

  searchModel = TeamMemberSearchModel;

  // initial ag grid state
  gridApi;
  defaultColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    floatingFilterComponentParams: {suppressFilterButton: true},
    minWidth: 200,
    flex: 2,
    sortable: true,
    resizable: true,
    suppressCsvExport: true,
  }

  columnsDefs = [
    {
      headerName: 'Email',
      field: 'main_email',
      cellStyle: {textAlign: 'left'}
    },
    {
      headerName: 'Mobile',
      field: 'main_phone',
      cellStyle: {textAlign: 'left'}
    },
    {
      headerName: 'Rank',
      field: `member.link_subject_to_grp.rank`,
      cellStyle: {textAlign: 'left'}
    }
  ];

  rowData = [
    // {
    //   email: 'vicky@wavelet.net',
    //   rank: 'OWNER',
    //   roles: '5'
    // },
    // {
    //   email: 'car@wavelet.net',
    //   rank: 'ADMIN',
    //   roles: '2'
    // }
  ];
  rowModelType = 'serverSide';
  serverSideStoreType = 'partial';

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  teamGuid: string;

  constructor(
    private linkSubjectToTeamService: LinkSubjectToTeamService,
    // private appLoginPrincipalService: AppLoginPrincipalService,
    private appLoginSubjectService: AppLoginSubjectService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
   ngOnInit() {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });

    const currentUserInTeam$ = this.store.select(TeamPermSelectors.selectCurrentUser)
      .pipe(map(x => x.link_subject_to_grps.map(y => y.app_grp_guid).includes(this.teamGuid)));
    const localStateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);

    this.subSink.sink = this.store.select(TeamPermSelectors.selectGuid).subscribe(x => {
      this.teamGuid = x;
      // deactivate add button if user is not in the team or if the local state deactivates the add button
      this.deactivateAdd$ = combineLatest([currentUserInTeam$, localStateAdd$])
      .pipe(
        map(([x, y]) => !x || y)
      )
      if (this.gridApi) {
        const datasource = this.getDatasource();
        this.gridApi.setServerSideDatasource(datasource);
      }
    })

    this.subSink.sink = this.store.select(TeamPermSelectors.selectUpdateMemberListing).subscribe(x => {
      if (x && this.gridApi) {
        if (this.gridApi.getDisplayedRowCount() > 0) {
          this.gridApi.refreshServerSideStore();
        } else {
          this.gridApi.refreshServerSideStore(
            {purge: true}
          )
        }
        this.store.dispatch(TeamPermActions.setUpdateMemberListing({update: false}));
      }
    })
  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
   onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateReturn: true,
        deactivateAdd: true,
        deactivateList: true
      });
    // trigger the create permissions template view column component to to be shown
    this.viewColFacade.onNext(2);
  }

    /**
   * Decides whether the data is being filtered based on the filterModel
   * provided by the AG grid request and returns what contains an object
   * that contains a function which takes in a view model and decides if
   * it contains the filter search string and a boolean whether the data is
   * currently being filtered or not.
   *
   * @param filterModel : the filter model provided by AG grid
   * @returns {by: a function, isFiltering: true if not currently filtered, false otherwise}
   */
     pageFiltering(filterModel){
      var noFilters = Object.keys(filterModel).length <= 0;
      if (noFilters) return {
        by : (viewModel) => true,
        isFiltering : noFilters
      }
      return {
        by: (viewModel) =>  Object
          .keys(filterModel)
          .map(col => {
            const fields = col.split(".")
            const val = fields.reduce(((acc, property) => acc ? acc[property] : null), viewModel)

            return filterModel.filterType == "number" ?
              +val === +filterModel[col].filter :
              val.toLowerCase().includes(filterModel[col].filter.toLowerCase())})
          .reduce((p, c)=> p && c),
        isFiltering: noFilters
      }
    }

    /**
     * Sorts data according to the sort model provided by AG grid and returns
     * this new data.
     *
     * @param sortModel : the sort model provided by AG grid
     * @returns the sorted data
     */
    pageSorting(sortModel){
      return (data) => {
        // return data if there is no sorting required
        if (sortModel.length <= 0) return data;
        // make a copy of data
        let newData = data.map(o => o);
        sortModel.forEach(model => {
          const col = model.colId;
          const fields = col.split(".")
          const getVal = (entity) => fields.reduce((acc, property) => acc ? acc[property] : null, entity)

          // sort the data
          newData = model.sort == 'asc' ?
            // p and c are of type VendorContainerModel
            newData.sort((p,c) => 0 - (getVal(p) > getVal(c) ? -1 : 1)) :
            newData.sort((p,c) => 0 - (getVal(p) > getVal(c) ? 1 : -1))}
        )

        return newData;
      }
    }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

    /**
   * Returns the datasource for the AG grid.
   *
   * @param criteria : any custom criteria to search for in the API
   * @returns the datasource
   */
  getDatasource(criteria?) {
    return {
      getRows: grid => {

        // dispatch action to load members
        this.store.dispatch(TeamPermActions.loadMembersInit({request: grid.request}));
        if (!criteria) {
          criteria = [
            {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
            {columnName: 'app_grp_guid', operator: '=', value: this.teamGuid}
          ]
        }

        const sortOn = this.pageSorting(grid.request.sortModel);
        const filter = this.pageFiltering(grid.request.filterModel);
        const offset = grid.request.startRow;
        const limit = grid.request.endRow;
        const pagination = new Pagination(offset, limit, criteria);

        this.subSink.sink = this.linkSubjectToTeamService.getByCriteria(pagination, this.apiVisa)
        .pipe(
          mergeMap(a => {
            const source: Observable<MemberModel>[] =[];
            a.data.forEach(b => {
              // create a new memberModel to display on the AG grid
              const member: MemberModel = new MemberModel();
              member.link_subject_to_grp = b.link_subject_to_grp;
              member.app_login_subject_guid = b.link_subject_to_grp.app_subject_guid.toString();

              const result$ = this.getUser(b.link_subject_to_grp.app_subject_guid.toString()).pipe(
                map((userFromApi) => {
                  // Combine the userModel with the userFromApi data
                  return { ...userFromApi, member };
                })
              );
              source.push(result$);
            })

            return iif(() => source.length > 0,
            forkJoin(source).pipe(map(x => {
              return {...a,
              data: x}
            })),
            of({...a, data: [], totalRecords: 0}))
          })
        ).subscribe(a => {
          // put data into the grid
          const data = sortOn(a.data).filter(entity => filter.by(entity));
          const totalRecords = filter.isFiltering ? a.totalRecords : data.length;

          grid.successCallback(data, totalRecords);
          this.store.dispatch(TeamPermActions.loadMembersSuccess());
        }, err => {
          grid.failCallback();
          this.store.dispatch(TeamPermActions.loadMembersFailure({error: err.message}));
        })
      }
    }
  }

  /**
   * Return the AppLoginPrincipalContainerModel of the subject with subjectGuid
   * as sepcified in the parameter.
   *
   * @param subjectGuid : the subject guid to the name of
   * @returns an observable of type AppLoginPrincipalContainerModel which
   * contains information of the subject
   */
  getUser(subjectGuid: string) {
    // initialise new pagination
    const pagination = new Pagination();
    // add necessary criteria
    pagination.conditionalCriteria.push({
      columnName: 'guids',
      operator: '=',
      value: subjectGuid
    });

    return this.appLoginSubjectService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map((subject) => {
          return subject.data[0]})
      )

  }

  /**
   * This function is called every time the user clicks on a row in the AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
   onRowClicked(entity) {

    // dispatch ngrx action to save selected member
    this.store.dispatch(TeamPermActions.selectMember({member: entity}));

    // deactivate add, return and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateReturn: true,
          deactivateAdd: true,
          deactivateList: true
        });
      // trigger the edit permissions template view column
      this.viewColFacade.onNext(3);
    }
  }

  /**
   * Set datasource on the AG grid to empty.
   */
   clear() {
    const dataSource = {
      getRows: grid => {
        grid.successCallback([], 0)
      }
    };

    this.gridApi.setServerSideDatasource(dataSource);
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
   /**
   * Runs the search bar's query against the team-members listing's own endpoint.
   *
   * Replaces the raw SQL that <app-advanced-search> built from
   * TeamMemberSearchModel and posted to SubQueryService (a SELECT over
   * app_mst_link_subject_to_grp joined to app_login_principal, with
   * `AND hdr.app_grp_guid = '<team>'` appended here). The search bar is now
   * <app-advanced-search-general>, which emits the keyword and the form value
   * instead of SQL.
   *
   * GET users-team/query -> LinkSubjectToTeamUow.generateWhereClauseByCriteria
   * joins app_login_principal itself and honours `search_word` (ILIKE over
   * principal_id, title and rank), `rank`, `app_grp_guid`, and defaults to
   * `status != 'DELETED'`.
   *
   * getDatasource only applies its defaults when called with no argument, so the
   * calcTotalRecords and app_grp_guid scoping is re-seeded here -- otherwise a
   * search would widen the listing to every team.
   *
   * Behaviour note: the keyword also matches the membership title and rank, not
   * just the principal id.
   */
  onSearch(e: SearchQueryModel) {
    const datasource = this.getDatasource(this.buildSearchCriteria(e));
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the emitted search, or undefined when it is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria(e: SearchQueryModel) {
    if (!e || e.isEmpty) {
      return undefined;
    }
    const scope = [
      { columnName: 'calcTotalRecords', operator: '=', value: 'true' },
      { columnName: 'app_grp_guid', operator: '=', value: this.teamGuid }
    ];
    if (e.keyword) {
      return scope.concat([
        { columnName: 'search_word', operator: '=', value: e.keyword.trim() }
      ]);
    }
    const query: any = e.queryString || {};
    return scope.concat(
      [{ columnName: 'rank', operator: '=', value: query.rank }]
        .filter(condition => condition.value)
    );
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
